const ITEM_DRAFT_PREFIX = 'addItemDraft:';
const CAMPAIGN_DRAFT_PREFIX = 'addCampaignDraft:';

export interface FormDraftPhoto {
  id: string;
  url: string;
  safe: boolean;
}

export interface ItemFormDraft {
  type?: string;
  photos: FormDraftPhoto[];
  videoUrl?: string;
  sellingMethod?: string;
  condition?: string;
  endDateTime?: string;
  price?: number;
  isNegotiable?: boolean;
  unitsQuantity?: number;
  itemLocation?: unknown;
  canLocalPickUpDelivery?: boolean;
  canNationwideShipping?: boolean;
  weightUpTo?: number;
  supportedCampaign?: unknown;
  title?: string;
  description?: string;
  category?: string;
  defaultShipFromAddressId?: number;
}

export interface CampaignFormDraft {
  photos: FormDraftPhoto[];
  videoUrl?: string;
  moneyGoal?: number;
  name?: string;
  campaignLocation?: unknown;
  description?: string;
  categories?: string[];
  timelineFrom?: string;
  timelineTo?: string;
}

const readDraft = <T>(key: string): T | undefined => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return undefined;
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
};

const writeDraft = (key: string, draft: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(draft));
  } catch {
    // ignore quota / private mode errors
  }
};

const clearDraft = (key: string) => localStorage.removeItem(key);

const itemKey = (userId: number) => `${ITEM_DRAFT_PREFIX}${userId}`;
const campaignKey = (userId: number) => `${CAMPAIGN_DRAFT_PREFIX}${userId}`;

export const getItemFormDraft = (userId: number) => readDraft<ItemFormDraft>(itemKey(userId));
export const saveItemFormDraft = (userId: number, draft: ItemFormDraft) => writeDraft(itemKey(userId), draft);
export const clearItemFormDraft = (userId: number) => clearDraft(itemKey(userId));

export const getCampaignFormDraft = (userId: number) => readDraft<CampaignFormDraft>(campaignKey(userId));
export const saveCampaignFormDraft = (userId: number, draft: CampaignFormDraft) =>
  writeDraft(campaignKey(userId), draft);
export const clearCampaignFormDraft = (userId: number) => clearDraft(campaignKey(userId));

export const toDraftPhotos = (photos: { id: string; url?: string; safe: boolean }[]): FormDraftPhoto[] =>
  photos.filter((photo): photo is FormDraftPhoto => !!photo.url).map(({ id, url, safe }) => ({ id, url, safe }));

export const isItemFormDraftMeaningful = (draft?: ItemFormDraft) => {
  if (!draft) return false;
  return !!(
    draft.type ||
    draft.sellingMethod ||
    draft.title?.trim() ||
    draft.description?.trim() ||
    draft.category?.trim() ||
    draft.condition?.trim() ||
    draft.photos?.length ||
    draft.videoUrl ||
    draft.price ||
    draft.itemLocation ||
    draft.supportedCampaign ||
    draft.defaultShipFromAddressId ||
    (draft.unitsQuantity && draft.unitsQuantity > 1)
  );
};

export const isCampaignFormDraftMeaningful = (draft?: CampaignFormDraft) => {
  if (!draft) return false;
  return !!(
    draft.name?.trim() ||
    draft.description?.trim() ||
    draft.photos?.length ||
    draft.videoUrl ||
    draft.moneyGoal ||
    draft.campaignLocation ||
    draft.categories?.length ||
    draft.timelineFrom ||
    draft.timelineTo
  );
};
